import { PhoneCall, ArrowRight, Clock, ShieldCheck } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, HERO_IMAGE } from "@/lib/site-content";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden bg-white border-b border-zinc-200"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMAGE}
          alt="Modern home with utility services"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/30 lg:to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 py-16 sm:py-24 lg:py-32 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-8 fade-up">
          <div className="inline-flex items-center gap-2 bg-zinc-900 text-white px-3 py-1.5 mb-6">
            <span className="w-1.5 h-1.5 bg-[#FF4500] animate-pulse rounded-full" />
            <span className="label-eyebrow text-white">Live agents · 24/7</span>
          </div>

          <h1 className="font-display font-black text-zinc-900 text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
            Electricity, gas &amp; water
            <span className="block text-[#FF4500]">handled in one call.</span>
          </h1>

          <p className="mt-6 max-w-xl text-base sm:text-lg text-zinc-700 leading-relaxed">
            Specpay Solutions sets up, transfers, stops and manages residential
            utility services across all 50 states. Skip the hold music — speak
            to a live specialist now.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
            <a
              href={PHONE_TEL}
              data-testid="hero-call-button"
              className="inline-flex items-center justify-center gap-3 bg-[#FF4500] hover:bg-[#E63E00] text-white font-display font-black tracking-tight px-8 py-5 text-lg transition-all active:scale-[0.97] shadow-lg shadow-orange-500/20"
            >
              <PhoneCall className="w-5 h-5" strokeWidth={2.7} />
              Call {PHONE_DISPLAY}
            </a>
            <a
              href="#request"
              data-testid="hero-request-button"
              className="inline-flex items-center justify-center gap-2 border-2 border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white font-display font-bold tracking-wide px-8 py-5 text-base transition-all active:scale-[0.97]"
            >
              Request Online
              <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
            </a>
          </div>

          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-zinc-600">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#FF4500]" strokeWidth={2.5} />
              <span className="font-medium">Same-day setup available</span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#FF4500]" strokeWidth={2.5} />
              <span className="font-medium">Licensed &amp; trusted</span>
            </div>
          </div>
        </div>

        <div className="hidden lg:block lg:col-span-4 fade-up">
          <div className="bg-zinc-900 text-white p-8 border-t-4 border-[#FF4500]">
            <p className="label-eyebrow text-zinc-400 mb-3">Need help right now?</p>
            <p className="font-display font-black text-3xl tracking-tight leading-tight">
              Call our specialists.
            </p>
            <p className="mt-3 text-zinc-300 text-sm leading-relaxed">
              No queues. No transfers. One number for every utility service —
              start, stop, move, pay or manage.
            </p>
            <a
              href={PHONE_TEL}
              data-testid="hero-side-call-button"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#FF4500] hover:bg-[#E63E00] text-white font-display font-bold py-4 transition-all active:scale-[0.97]"
            >
              <PhoneCall className="w-5 h-5" strokeWidth={2.7} />
              {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
